import { OktaUserType } from './../server/schema/common/apolloContextTypes';
import { EdiTenderStatusTypeId, EdiTenderTypeId, LoadTypeId } from './../src/constants/loadConstants/loadConstants';
import { TenderType } from './../sharedTypes/loads/loadType';
import { EDI_USER_ID } from './../src/constants/userConstants';
import type { CanBookOrOfferUserType, CurrentUserType } from 'sharedTypes/user/userTypes';
import type { LoadType } from 'sharedTypes/loads/loadType';
import { getRealTimezoneMoment } from './timeUtils';
import moment from 'moment-timezone';

export const isArriveInternalUser = (currentUser: { CarrierId?: number; EmailAddress: string }) => {
  return !currentUser.CarrierId && currentUser.EmailAddress.includes('@arrivelogistics.com');
}

// used in load.ts to determine if we return Forbidden error
export const canViewLoadDetails = (load: any, user: CurrentUserType) => {
  const carrierDidNotBookLoad = load.LoadStatus !== 'Available' && user.CarrierId && !load.Carriers.some((carrier: { CarrierId: number }) => carrier ? carrier.CarrierId === user.CarrierId : true)
  const isAssignedToUser = load.LoadStatus === 'Assigned' && load.AssignedToUserId === user.UserId;
  if (isArriveInternalUser(user) && load.LoadStatus !== 'Void') {
    return true;
  }

  if (isAssignedToUser) {
    return true;
  }

  return load.LoadStatus !== 'Void' && !carrierDidNotBookLoad && load.LoadStatus !== 'Assigned';
}

export const hasPassedSameDayWindow = (load: LoadType) => {
  const { PickupApptLatest, PickupDateLatest, PickupLocationIANACode } = load;
  const rawPickupLate = PickupApptLatest || PickupDateLatest;
  const pickupLatestMoment = getRealTimezoneMoment(rawPickupLate, PickupLocationIANACode)
  const nowMoment = moment();
  const differenceInHours = pickupLatestMoment.diff(nowMoment, 'hours', true)
  // includes a hour buffer - so if a load picks up at 2pm they can not book through the portal after 1pm
  return differenceInHours <= 1;
};

export const withinOneHourWindow = (load: LoadType) => {
  const { PickupApptLatest, PickupDateLatest, PickupLocationIANACode } = load;
  const rawPickupLate = PickupApptLatest || PickupDateLatest;
  const pickupLatestMoment = getRealTimezoneMoment(rawPickupLate, PickupLocationIANACode)
  const nowMoment = moment();
  const differenceInHours = pickupLatestMoment.diff(nowMoment, 'hours', true)
  return differenceInHours < 1 && differenceInHours > 0;
}

// BOOK IT NOW PERMISSIONS
// when adding new parameters to these utils ensure the that the Price filter in schema/load/loadHelpers also filters out these loads
const canLoadBeBookItNow = (load: LoadType) => {
  return load.IsBookItNow && load.PickupApptStatusConfirmed;
}

export const doesInsuranceExpireFromNow = (currentUser: CurrentUserType) => {

  const { CargoInsExpirationDate, LiabilityInsExpirationDate, LiabilityInsRenewalEffectiveDate, CargoInsRenewalEffectiveDate } = currentUser;
  if (!currentUser.UserId) {
    return false;
  }

  if (!CargoInsExpirationDate || !LiabilityInsExpirationDate) {
    return true;
  }

  const cargoInsDate = moment(CargoInsExpirationDate);
  const liabilityInsDate = moment(LiabilityInsExpirationDate);

  const cargoDiff = Math.abs(moment().diff(cargoInsDate, 'days'));
  const liabilityDiff = Math.abs(moment().diff(liabilityInsDate, 'days'));

  return (cargoDiff <= 7 && !CargoInsRenewalEffectiveDate) || (liabilityDiff <= 7 && !LiabilityInsRenewalEffectiveDate)
}

const doesInsuranceExpire = (currentUser: { CargoInsExpirationDate: string, LiabilityInsExpirationDate: string, LiabilityInsRenewalEffectiveDate: string, CargoInsRenewalEffectiveDate: string }, load: LoadType) => {
  const { CargoInsExpirationDate, LiabilityInsExpirationDate, LiabilityInsRenewalEffectiveDate, CargoInsRenewalEffectiveDate } = currentUser;

  const { PickupApptEarliest, PickupDateEarliest, PickupLocationIANACode } = load;
  const rawPickupDate = PickupApptEarliest || PickupDateEarliest;
  // If no insurance expiry or PickupEarliest, contact rep
  if (!CargoInsExpirationDate || !LiabilityInsExpirationDate || !rawPickupDate) {
    return true;
  }
  const pickupDate = getRealTimezoneMoment(rawPickupDate, PickupLocationIANACode);
  const cargoInsDate = moment(CargoInsExpirationDate);
  const liabilityInsDate = moment(LiabilityInsExpirationDate);
  const formattedPu = pickupDate.format('L');
  const cargoDiff = Math.abs(moment(formattedPu).diff(cargoInsDate, 'days'));
  const liabilityDiff = Math.abs(moment(formattedPu).diff(liabilityInsDate, 'days'));

  return (cargoDiff <= 7 && !CargoInsRenewalEffectiveDate) || (liabilityDiff <= 7 && !LiabilityInsRenewalEffectiveDate)
};

export const reeferWithoutReeferBreakdown = (load: { LoadType?: any, LoadTypeId?: LoadTypeId, EquipmentType?: string }, currentUser: { HasReeferIns: boolean }) => {
  if ((load.LoadType === 'Reefer' || load.LoadTypeId === LoadTypeId.REEFER || load.EquipmentType === 'R' ) && !currentUser.HasReeferIns) {
    return true;
  }
  return false;
};

const checkHRHVCert = (currentUser: CanBookOrOfferUserType, load: LoadType) => {
  const isCertified = currentUser.IsHighRiskCertified;
  const bookableCargoAmountCeiling = 250000;
  const onlyHR = (load.IsHighRisk && !load.IsHighValue);
  const nonHRHV = (!load.IsHighRisk && !load.IsHighValue);
  const canBookHV = (!load.IsHighRisk && load.CargoValue < bookableCargoAmountCeiling);

  if(isCertified) {
    return nonHRHV || onlyHR || canBookHV;
  }
  else {
    return nonHRHV
  }
}

export const canUserBookOrOffer = (currentUser: CanBookOrOfferUserType, load: LoadType) => {
  const hazmatCompliant = load.IsHazmat ? currentUser.IsHazmatCertified : true;
  const autoTrackingCompliant = load.CustomerTrackingFrequencyTypeId !== 1 ? currentUser.HasAutoTracking : true;

  return currentUser.IsCompliant && hazmatCompliant && autoTrackingCompliant && checkHRHVCert(currentUser, load) && !doesInsuranceExpire(currentUser, load) && !hasPassedSameDayWindow(load) && !reeferWithoutReeferBreakdown(load, currentUser);
}

const canUserBookItNow = (currentUser: CurrentUserType, load: LoadType) => {
  return canLoadBeBookItNow(load) && canUserBookOrOffer(currentUser, load);
}

export const canSeeBookItNow = (currentUser: CurrentUserType, load: LoadType) => {
  return !isArriveInternalUser(currentUser) && canUserBookItNow(currentUser, load);
}

export const canSeeBookItPrices = (currentUser: CurrentUserType, load: LoadType) => {
  return isArriveInternalUser(currentUser) ? canLoadBeBookItNow(load) : canUserBookItNow(currentUser, load);
}

export const canSeeMakeAnOffer = (currentUser: CurrentUserType, load: LoadType) => {
  return isArriveInternalUser(currentUser) ? false : canUserBookOrOffer(currentUser, load);
}

export const isMyEdiAssignedLoad = (load: { LoadStatus: string; AssignedToUserId: number; Carriers: Array<{ CarrierId: number }> }, currentUser: CurrentUserType) => {
  const isEDIUser = currentUser.ConnectionMethodTypeId = 2
  const isEDILoad = load.AssignedToUserId === EDI_USER_ID
  const assignedToCarrier = load.Carriers?.find(((carrier) => {
    return carrier?.CarrierId === currentUser?.CarrierId
  }))

  return !!(isEDIUser && isEDILoad && assignedToCarrier)
}

const validTenderTypeIds = [EdiTenderTypeId.NewTender, EdiTenderTypeId.Update]
export const isValidTender = (tender: TenderType) => {
  return tender && validTenderTypeIds.includes(tender.EdiLoadTenderTypeId) && tender.EdiLoadTenderStatusTypeId === EdiTenderStatusTypeId.Unprocessed
}

export const showDedicatedOpportunities = (user: CurrentUserType, loading: boolean, flag?: boolean, unitCountFlag?: boolean) => {
  if(!loading && user.OktaId) {
    const unitCountCheck = user.UnitCount >= 12 || unitCountFlag;
    return unitCountCheck && user.IsCompliant && flag
  }
  return true
};

export const showCommittedCapacity = (user: CurrentUserType, loading?: boolean, flag?: boolean) => {
  if(!loading && user.OktaId) {
    return flag
  }
  return true
};